'use client';

import React from 'react';

interface UserAvatarProps {
  name?: string;
  email?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
  profileImage?: string; // GridFS file ID for profile image
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  email,
  size = 'md',
  className = '',
  onClick,
  profileImage,
}) => {
  // Generate initials from name or email
  const getInitials = (): string => {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        // Take first letter of first and last name
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      // If only one name, take first two letters
      return name.slice(0, 2).toUpperCase();
    }

    if (email) {
      // Use first two letters of email before @
      const emailName = email.split('@')[0];
      return emailName.slice(0, 2).toUpperCase();
    }

    // Default fallback
    return 'US';
  };

  // Size classes for different sizes
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-20 h-20 sm:w-24 sm:h-24 text-lg sm:text-xl',
  };

  // Generate a consistent background color based on the name/email
  const getBackgroundColor = (): string => {
    const str = name || email || 'default';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Generate a hue between 200-280 (blues and purples) to match your theme
    const hue = 200 + (Math.abs(hash) % 80);
    return `hsl(${hue}, 70%, 50%)`;
  };

  const initials = getInitials();
  const backgroundColor = getBackgroundColor();

  // If profile image exists, show it, otherwise show initials
  if (profileImage) {
    return (
      <div data-component-id="src/components/common/UserAvatar.tsx:71:6" data-component-path="src/components/common/UserAvatar.tsx" data-component-line="71" data-component-file="UserAvatar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`rounded-full overflow-hidden ${sizeClasses[size]} ${className}`}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <img data-component-id="src/components/common/UserAvatar.tsx:77:8" data-component-path="src/components/common/UserAvatar.tsx" data-component-line="77" data-component-file="UserAvatar.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%22%7D"
          src={`/api/files/${profileImage}`}
          alt={name || 'Profile'}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to initials if image fails to load
            const target = e.currentTarget;
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = `
                <div class="rounded-full flex items-center justify-center font-semibold text-[#FFFFFF] w-full h-full" style="background-color: ${backgroundColor}">
                  ${initials}
                </div>
              `;
            }
          }}
        />
      </div>
    );
  }

  return (
    <div data-component-id="src/components/common/UserAvatar.tsx:99:4" data-component-path="src/components/common/UserAvatar.tsx" data-component-line="99" data-component-file="UserAvatar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`rounded-full flex items-center justify-center font-semibold text-[#FFFFFF] ${sizeClasses[size]} ${className}`}
      style={{ backgroundColor }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {initials}
    </div>
  );
};

export default UserAvatar;
